import { DeliveryMode, DeliveryProvider } from '../exports/Constants';
import { ManagerRole } from '../permissions/PermissionResolver';

/**
 * Wolt Drive onboarding (PROD-491): the decisions behind the onboarding card on
 * Order settings → Delivery, kept out of the component so they can be tested.
 */

export const WoltDriveProviderPriority = Object.freeze({
  WoltPrimary: 'wolt_primary',
  WoltOnly: 'wolt_only',
});

// Wolt venue IDs are MongoDB ObjectIds — the backend enforces the same pattern.
const WOLT_VENUE_ID_PATTERN = /^[a-f0-9]{24}$/;

/** What the manager typed or pasted, as the backend will validate it. */
export function normaliseWoltVenueId(value) {
  return (value ?? '').trim().toLowerCase();
}

export function isValidWoltVenueId(value) {
  return WOLT_VENUE_ID_PATTERN.test(normaliseWoltVenueId(value));
}

/** `6736f9f5f9e2ab82f9150420` → `6736f9…0420`: enough to recognise, not to copy. */
export function maskWoltVenueId(venueId) {
  if (!venueId) {
    return '';
  }

  return venueId.length > 10 ? `${venueId.slice(0, 6)}…${venueId.slice(-4)}` : venueId;
}

/**
 * Super admins, and onboarding managers of the platform itself. A partner can
 * staff its own "onboarding manager", but the Wolt merchant account behind
 * every venue is the platform's, so the role alone is not enough.
 */
export function canManageWoltDriveOnboarding({ isSuperAdmin, roles = [], isMasterProvider }) {
  return isSuperAdmin === true || (roles.includes(ManagerRole.OnboardingManager) && isMasterProvider === true);
}

/** The company's own region decides, as Company::hasWoltDriveIntegration() does on the backend. */
export function regionSupportsWoltDrive(company) {
  return (company?.regional_config?.supported_delivery_providers ?? []).includes(DeliveryProvider.WoltDrive);
}

/**
 * A stored venue ID Wolt can use. Some restaurants carry one that is not (a
 * 25-character ID typed in by hand), which Wolt never quotes — for them the
 * venue ID is as good as missing.
 */
export function hasValidWoltVenueId(company) {
  return isValidWoltVenueId(company?.wolt_drive_venue_id);
}

/**
 * The onboarding card is for restaurants still waiting on Wolt: once a valid
 * venue ID is stored — which only a verified save does — there is nothing left
 * to do there.
 */
export function needsWoltDriveOnboarding(company) {
  return regionSupportsWoltDrive(company) && !hasValidWoltVenueId(company);
}

/**
 * Whether the provider switch can be used at all. Only Wolt is locked: without
 * a valid venue ID it is skipped at runtime, and the onboarding card is the way
 * to give it one.
 */
export function isDeliveryProviderProvisioned(company, provider) {
  if (provider === DeliveryProvider.WoltDrive) {
    return hasValidWoltVenueId(company);
  }

  return true;
}

/**
 * The per-restaurant account each external provider delivers through. Uber's
 * customer and FirstDelivery's merchant are created by the backend when the
 * restaurant switches to external delivery (SwitchDeliveryProvider).
 */
const PROVIDER_ACCOUNT_FIELD = Object.freeze({
  [DeliveryProvider.UberDrive]: 'uber_drive_customer_id',
  [DeliveryProvider.FirstDelivery]: 'first_delivery_merchant_id',
});

/**
 * Why a listed provider does not deliver for this restaurant yet, mirroring
 * Company::hasDeliveryIntegration() — the key of the tooltip to show, or null
 * when it is connected. The saved delivery mode decides between "connects on
 * the switch" and "should have connected by now".
 */
export function deliveryProviderNotice(company, provider) {
  if (provider === DeliveryProvider.WoltDrive) {
    return hasValidWoltVenueId(company) ? null : 'wolt_drive';
  }

  const accountField = PROVIDER_ACCOUNT_FIELD[provider];

  if (!accountField || company?.[accountField]) {
    return null;
  }

  return company?.delivery_mode === DeliveryMode.External ? 'not_connected_yet' : 'connects_on_external';
}

/**
 * The integration names, not the brand a merchant sees: DELIVERY_BRAND_BY_PROVIDER
 * shows FirstDelivery as "Uber" too, which would make `Uber › Uber` of a list
 * the onboarding manager has to tell apart.
 */
const PROVIDER_LABELS = Object.freeze({
  [DeliveryProvider.WoltDrive]: 'Wolt Drive',
  [DeliveryProvider.UberDrive]: 'Uber Direct',
  [DeliveryProvider.FirstDelivery]: 'FirstDelivery',
  [DeliveryProvider.Standalone]: 'Standalone',
});

/** `['wolt_drive', 'uber_drive']` → `Wolt Drive › Uber Direct` — the order the providers are tried in. */
export function formatDeliveryProviderChain(providers, emptyLabel = '—') {
  if (!providers?.length) {
    return emptyLabel;
  }

  return providers.map((provider) => PROVIDER_LABELS[provider] ?? provider).join(' › ');
}
